import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, IconButton, Button, TextField, Tooltip, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const AccountCardPkudaExportModal = ({ 
    open, 
    onClose, 
    previewData,
    editableAccountCards,
    setEditableAccountCards,
    onConfirmExport,
    direction,
    t 
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [palCodeMappings, setPalCodeMappings] = useState({});
    const [mappingApplied, setMappingApplied] = useState(false);
    const itemsPerPage = 100;

    // Extract unique PalCodes with their counts - MUST be before early return
    const palCodeGroups = useMemo(() => {
        if (!previewData?.cards) return [];
        
        const groups = {};
        previewData.cards.forEach(card => {
            const palCode = card.currentPalCode || 'לא מוגדר';
            if (!groups[palCode]) {
                groups[palCode] = {
                    palCode: palCode,
                    count: 0,
                    suggestedExportCode: card.suggestedColumn26 || ''
                };
            }
            groups[palCode].count++;
        });
        
        return Object.values(groups).sort((a, b) => b.count - a.count);
    }, [previewData]);

    // Pagination for preview - MUST be before early return
    const totalPages = Math.ceil((editableAccountCards?.length || 0) / itemsPerPage);
    const paginatedCards = useMemo(() => {
        if (!editableAccountCards) return [];
        const startIndex = (currentPage - 1) * itemsPerPage;
        return editableAccountCards.slice(startIndex, startIndex + itemsPerPage);
    }, [editableAccountCards, currentPage, itemsPerPage]);

    // Initialize mappings when modal opens
    useEffect(() => {
        if (open && palCodeGroups.length > 0) {
            const initialMappings = {};
            palCodeGroups.forEach(group => {
                initialMappings[group.palCode] = group.suggestedExportCode;
            });
            setPalCodeMappings(initialMappings);
            setCurrentPage(1);
            setMappingApplied(false);
        }
    }, [open, palCodeGroups]);

    // Early return AFTER all hooks
    if (!open || !previewData) return null;

    // Apply PalCode mappings to all cards
    const applyPalCodeMappings = () => {
        const updatedCards = editableAccountCards.map(card => ({
            ...card,
            exportCode: palCodeMappings[card.currentPalCode] || card.column26
        }));
        setEditableAccountCards(updatedCards);
        setMappingApplied(true);
    };

    // Update PalCode mapping
    const updatePalCodeMapping = (palCode, value) => {
        setPalCodeMappings(prev => ({
            ...prev,
            [palCode]: value
        }));
        setMappingApplied(false); // Mark as not applied when mapping changes
    };

    return (
        <div className="download-modal-overlay" onClick={onClose}>
            <div className="download-modal" onClick={(e) => e.stopPropagation()}>
                <div 
                    className={`download-modal-content ${direction}`} 
                    style={{ 
                        position: 'relative', 
                        maxWidth: '900px', 
                        maxHeight: '90vh', 
                        overflow: 'auto' 
                    }}
                >
                    {/* Header */}
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        marginBottom: '20px',
                        position: 'sticky',
                        top: 0,
                        backgroundColor: 'white',
                        zIndex: 1,
                        paddingBottom: '10px'
                    }}>
                        <h4 className="report-title">תצוגה מקדימה - כרטיסי חשבון PKUDA</h4>
                        <IconButton 
                            className="client-back-icon" 
                            onClick={onClose}
                            sx={{ padding: '4px' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    {/* Summary Info */}
                    <Box sx={{ 
                        p: 2.5, 
                        backgroundColor: previewData.totalCards > 0 ? '#d4edda' : '#fff3cd', 
                        borderRadius: '8px',
                        border: previewData.totalCards > 0 ? '1px solid #28a745' : '1px solid #ffc107',
                        mb: 3
                    }}>
                        <Typography variant="h6" sx={{ 
                            color: previewData.totalCards > 0 ? '#155724' : '#856404', 
                            mb: 1.5,
                            fontWeight: 600
                        }}>
                            {previewData.totalCards > 0 ? '✓ כרטיסים מוכנים לייצוא' : '⚠ אזהרה'}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography sx={{ fontWeight: 500 }}>סוג ניהול:</Typography>
                            <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>
                                {previewData.accountManagement}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ fontWeight: 500 }}>סה"כ כרטיסים:</Typography>
                            <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#304FFF' }}>
                                {previewData.totalCards}
                            </Typography>
                        </Box>

                        <Typography sx={{ 
                            fontSize: '14px', 
                            color: '#155724',
                            fontWeight: 500,
                            mt: 2,
                            pt: 2,
                            borderTop: '1px solid rgba(0,0,0,0.1)'
                        }}>
                            {previewData.message}
                        </Typography>
                    </Box>

                    {/* PalCode Mapping Section */}
                    {previewData.totalCards > 0 && (
                        <>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                    שלב 1: מיפוי קודי פל
                                </Typography>
                                
                                {!mappingApplied && (
                                    <Box sx={{ 
                                        p: 2, 
                                        mb: 2, 
                                        backgroundColor: '#FFF9E6', 
                                        borderRadius: '8px',
                                        border: '1px solid #F9AA2A'
                                    }}>
                                        <Typography sx={{ fontSize: '14px', color: '#856404', fontWeight: 500 }}>
                                            💡 הגדר את ערכי העמודות לכל סיווג מאזני, ולאחר מכן לחץ "החל מיפוי" להחיל על כל הכרטיסים
                                        </Typography>
                                    </Box>
                                )}

                                <Box sx={{ 
                                    border: '1px solid #E5E7EB',
                                    borderRadius: '8px',
                                    overflow: 'hidden'
                                }}>
                                    <table className="sortcode-table editable" style={{ width: '100%' }}>
                                        <thead style={{ backgroundColor: '#F8F9FF' }}>
                                            <tr>
                                                <th style={{ width: '35%', padding: '14px' }}>סיווג מאזני</th>
                                                <th style={{ width: '20%', padding: '14px', textAlign: 'center' }}>מספר כרטיסים</th>
                                                <th style={{ width: '45%', padding: '14px' }}>
                                                    <Tooltip title="מספר הקוד שייוצא לקובץ PKUDA" placement="top">
                                                        <span>קוד ייצוא PKUDA ⓘ</span>
                                                    </Tooltip>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {palCodeGroups.map(group => (
                                                <tr key={group.palCode}>
                                                    <td style={{ padding: '12px' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>
                                                                {group.palCode}
                                                            </Typography>
                                                        </Box>
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                                        <Chip
                                                            label={group.count}
                                                            size="medium"
                                                            sx={{ 
                                                                backgroundColor: '#304FFF',
                                                                color: 'white',
                                                                fontWeight: 700,
                                                                fontSize: '14px',
                                                                minWidth: '60px'
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '12px' }}>
                                                        <TextField
                                                            fullWidth
                                                            value={palCodeMappings[group.palCode] || ''}
                                                            onChange={(e) => updatePalCodeMapping(group.palCode, e.target.value)}
                                                            variant="outlined"
                                                            size="medium"
                                                            placeholder="הזן קוד ייצוא (למשל: 1, 20, 99)"
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'white',
                                                                    fontSize: '15px',
                                                                    fontWeight: 500,
                                                                    '&:hover fieldset': {
                                                                        borderColor: '#304FFF',
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: '#304FFF',
                                                                        borderWidth: '2px'
                                                                    },
                                                                }
                                                            }}
                                                            inputProps={{
                                                                style: {
                                                                    textAlign: 'center',
                                                                    fontSize: '15px'
                                                                },
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Box>

                                {/* Apply Mapping Button */}
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        onClick={applyPalCodeMappings}
                                        disabled={mappingApplied}
                                        sx={{ minWidth: '200px' }}
                                    >
                                        {mappingApplied ? '✓ המיפוי הוחל' : 'החל מיפוי'}
                                    </Button>
                                </Box>
                            </Box>
                        </>
                    )}

                    {/* Preview Table Section */}
                    {previewData.totalCards > 0 && mappingApplied && (
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    שלב 2: תצוגה מקדימה
                                </Typography>
                                <Typography sx={{ fontSize: '14px', color: '#666' }}>
                                    מציג {paginatedCards.length} מתוך {editableAccountCards.length} כרטיסים
                                </Typography>
                            </Box>

                            <Box sx={{ 
                                maxHeight: '350px', 
                                overflow: 'auto',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px'
                            }}>
                                <table className="sortcode-table editable" style={{ width: '100%', fontSize: '13px' }}>
                                    <thead style={{ 
                                        position: 'sticky', 
                                        top: 0, 
                                        backgroundColor: '#F8F9FF',
                                        zIndex: 1
                                    }}>
                                        <tr>
                                            <th style={{ width: '18%', padding: '10px 8px' }}>מספר כרטיס</th>
                                            <th style={{ width: '40%', padding: '10px 8px' }}>שם כרטיס</th>
                                            <th style={{ width: '20%', padding: '10px 8px', textAlign: 'center' }}>סיווג מאזני</th>
                                            <th style={{ width: '22%', padding: '10px 8px', textAlign: 'center' }}>קוד ייצוא</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedCards.map((card) => (
                                            <tr key={card.cardId}>
                                                <td style={{ padding: '8px' }}>
                                                    <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                                                        {card.cardId}
                                                    </Typography>
                                                </td>
                                                <td style={{ padding: '8px' }}>
                                                    <Typography sx={{ fontSize: '13px' }}>
                                                        {card.name}
                                                    </Typography>
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                                    <Chip
                                                        label={card.currentPalCode}
                                                        size="small"
                                                        sx={{ 
                                                            backgroundColor: '#E8F4FD',
                                                            border: '1px solid #90CAF9',
                                                            fontSize: '12px',
                                                            fontWeight: 500
                                                        }}
                                                    />
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                                    <Chip
                                                        label={card.exportCode || card.column26}
                                                        size="small"
                                                        sx={{ 
                                                            backgroundColor: '#E8F5E9',
                                                            border: '1px solid #4CAF50',
                                                            fontWeight: 700,
                                                            fontSize: '14px',
                                                            minWidth: '60px'
                                                        }}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Box>

                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center',
                                    gap: 2,
                                    mt: 2
                                }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    >
                                        ← הקודם
                                    </Button>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                        עמוד {currentPage} מתוך {totalPages}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        disabled={currentPage === totalPages}
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    >
                                        הבא →
                                    </Button>
                                </Box>
                            )}

                        </Box>
                    )}

                    {/* Help Text */}
                    {previewData.totalCards > 0 && (
                        <Box sx={{ 
                            mt: 2, 
                            p: 2, 
                            backgroundColor: '#F8F9FF', 
                            borderRadius: '8px',
                            border: '1px solid #E5E7EB'
                        }}>
                            <Typography sx={{ fontSize: '13px', color: '#666', mb: 1 }}>
                                💡 <strong>מדריך שימוש:</strong>
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#666' }}>
                                1️⃣ הגדר ערכי עמודות לכל סיווג מאזני בטבלה העליונה
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#666' }}>
                                2️⃣ לחץ "החל מיפוי" כדי להחיל את המיפוי על כל הכרטיסים
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#666' }}>
                                3️⃣ בדוק את התוצאה בטבלת התצוגה המקדימה
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#666', mt: 1 }}>
                                <strong>קודי ייצוא נפוצים:</strong>
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#666' }}>
                                • 1 = לקוחות
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#666' }}>
                                • 20 = ספקים
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#666' }}>
                                • 99 = אחר / חד צדדית
                            </Typography>
                        </Box>
                    )}

                    {/* Action Buttons */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        justifyContent: 'flex-end',
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: 'white',
                        paddingTop: '15px',
                        borderTop: '1px solid #E5E7EB',
                        marginTop: '20px'
                    }}>
                        <Button 
                            variant="outlined" 
                            onClick={onClose}
                            sx={{ minWidth: '120px' }}
                        >
                            {t('Cancel') || 'ביטול'}
                        </Button>
                        <Button 
                            variant="contained" 
                            color="success"
                            onClick={onConfirmExport}
                            disabled={previewData.totalCards === 0}
                            sx={{ minWidth: '120px' }}
                        >
                            {t('ConfirmExport') || 'אישור וייצוא'}
                        </Button>
                    </Box>
                </div>
            </div>
        </div>
    );
};

export default AccountCardPkudaExportModal;
